import { isOfficialLoginMethod, LOGIN_METHOD, LoginRequest, Session } from '@gofranz/common';
import {
  LoginCallbackPage,
  LoginPage,
  MagicLinkLoginPage,
  NewsPage,
  PrivacyPage,
  SignupPage,
  SupportPage,
  TermsPage,
  useLanguageAwareRouting,
} from '@gofranz/common-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { AccountBillingUsagePage } from '../../pages/Account/Billing/Usage.page';
import { AccountDocsPage } from '../../pages/Account/Docs.page';
import { AccountProfilePage } from '../../pages/Account/Profile.page';
import { ChatPage } from '../../pages/Chat.page';
import { DocsPage } from '../../pages/Docs.page';
import { HomePage } from '../../pages/Home.page';
import { PricingPage } from '../../pages/Pricing.page';
import { useRustyState } from '../../state';
import classes from '../Common/Title.module.css';
import { Footer } from './Footer';
import { useComputedColorScheme } from '@mantine/core';

interface LanguageRoutesProps {
  languagePrefix?: string;
}

export function LanguageRoutes({ languagePrefix }: LanguageRoutesProps) {
  const { i18n } = useTranslation();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';

  // Update language when language prefix changes
  React.useEffect(() => {
    if (languagePrefix && languagePrefix !== i18n.language) {
      i18n.changeLanguage(languagePrefix);
    } else if (!languagePrefix && i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [languagePrefix, i18n]);

  const setSession = (session: Session) => {
    useRustyState.getState().api.auth?.setSession(session);
  };

  // For nested routes, React Router automatically strips the parent path
  // So when we match `/pt/*`, the nested routes should start from `/`
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/chat" element={<ChatPage />} />
      <Route
        path="/news"
        element={<NewsPage blogBaseUrl="https://blog.gpt-muse.com" footer={<Footer />} />}
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route
        path="/login"
        element={
          <LoginPage
            login={async (loginRequest: LoginRequest) => {
              if (isOfficialLoginMethod(loginRequest.type)) {
                return await useRustyState.getState().api.auth!.login(loginRequest);
              }
              throw new Error('Unsupported login method');
            }}
            loginChallenge={useRustyState.getState().loginChallenge}
            titleClassName={classes.title}
            onLoginSuccess={() => {
              nav(createLanguageURL('/chat'));
            }}
            backgroundImage={isDark ? '/login-background_dark.jpg' : '/login-background.jpg'}
            loginMethods={[LOGIN_METHOD.NOSTR, LOGIN_METHOD.GITHUB, LOGIN_METHOD.EMAIL_MAGIC_LINK]}
          />
        }
      />
      <Route
        path="/login/callback"
        element={
          <LoginCallbackPage
            setSession={setSession}
            titleClassName={classes.title}
            onLoginSuccess={() => {
              nav(createLanguageURL('/chat'));
            }}
          />}
      />
      <Route
        path="/login/magic-link"
        element={
          <MagicLinkLoginPage
            loginChallenge={useRustyState.getState().loginChallenge}
            titleGradient={{ from: 'blue', to: 'teal' }}
            titleClassName={classes.title}
            onLoginSuccess={() => {
              nav(createLanguageURL('/chat'));
            }}
          />
        }
      />
      <Route
        path="/privacy"
        element={<PrivacyPage supportEmail="support@gpt-muse.com" footer={<Footer />} />}
      />
      <Route
        path="/terms"
        element={<TermsPage supportEmail="support@gpt-muse.com" footer={<Footer />} />}
      />
      <Route path="/signup" element={<SignupPage
        backgroundImage={isDark ? '/login-background_dark.jpg' : '/login-background.jpg'}
        loginMethods={[LOGIN_METHOD.NOSTR, LOGIN_METHOD.GITHUB, LOGIN_METHOD.EMAIL_MAGIC_LINK]}
      />} />
      <Route path="/account/billing/usage" element={<AccountBillingUsagePage />} />
      <Route path="/account/docs" element={<AccountDocsPage />} />
      <Route
        path="/account/profile"
        element={
          <AccountProfilePage serviceDomain="gpt-muse.com" serviceEmail="hello@gpt-muse.com" />
        }
      />
      <Route
        path="/account/support"
        element={<SupportPage session={useRustyState.getState().api?.auth?.getSession()} serviceName="GPTMuse" />}
      />
    </Routes>
  );
}
